import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { FileInputComponent } from '@catch/file-input';
import { TooltipDirective } from '@catch/directives';
import {
  ButtonComponent,
  ChevronState,
  DropdownComponent,
  EmptyPlaceholderComponent,
  InfoBannerComponent,
  ModalComponent,
  RotatingChevronComponent,
  TableSearchInputComponent,
  TagComponent,
  TagVariant,
} from '@catch/general-ui';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faEye,
  faFile,
  faFileCertificate,
  faFileLines,
  faFileUser,
  faPaperclip,
  faPlus,
  faStar,
  faTrash,
  faUpload,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MeterGroupModule } from 'primeng/metergroup';
import type { MeterItem } from 'primeng/types/metergroup';
import { TableModule } from 'primeng/table';
import { fileTypes } from '../../../../../constants/filetypes';
import { PlatformDocumentViewerModalComponent } from '../../../../components/document-viewer-modal/platform-document-viewer-modal.component';
import {
  PlatformDocument,
  PlatformDocumentApplicationUsage,
  PlatformImportedCv,
} from '../../../../models/platform.models';
import { PlatformDocumentKind } from '../../../../enums/platform-document-kind.enum';
import { PlatformDocumentUploadType } from '../../../../enums/platform-document-upload-type.enum';
import { PlatformImportedCvAction } from '../../../../enums/platform-imported-cv-action.enum';
import { PlatformProfileService } from '../../../../services/platform-profile.service';

/** Monthly document-parsing allowance (mirrors backend `CANDIDATE_DOCUMENT_PARSINGS_PER_MONTH`). */
const DOCUMENT_PARSINGS_PER_MONTH = 5;

const PARSINGS_METER_COLORS = {
  available: 'var(--secondary)',
  used: '#c6c7cb',
} as const;

enum PlatformDocumentTableRowSource {
  DOCUMENT = 'document',
  PENDING = 'pending',
}

enum PlatformDocumentUploadMode {
  REGULAR = 'regular',
  STANDARD_MAIN = 'standard-main',
}

interface PlatformDocumentTableRow {
  id: number;
  name: string;
  source: PlatformDocumentTableRowSource;
  document: PlatformDocument | null;
  pending: PlatformImportedCv | null;
}

interface PlatformDocumentTypeOption {
  kind: PlatformDocumentKind;
  labelKey: string;
  icon: IconDefinition;
  /** When true, the document becomes the standard CV. */
  asMain: boolean;
  uploadType: PlatformDocumentUploadType;
}

@Component({
  selector: 'app-platform-profile-documents',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslocoPipe,
    TableModule,
    MeterGroupModule,
    FileInputComponent,
    TagComponent,
    ButtonComponent,
    DropdownComponent,
    RotatingChevronComponent,
    EmptyPlaceholderComponent,
    InfoBannerComponent,
    ModalComponent,
    TableSearchInputComponent,
    FaIconComponent,
    TooltipDirective,
    PlatformDocumentViewerModalComponent,
  ],
  templateUrl: './platform-profile-documents.component.html',
  styleUrl: './platform-profile-documents.component.scss',
})
export class PlatformProfileDocumentsComponent {
  private readonly profile = inject(PlatformProfileService);
  private readonly transloco = inject(TranslocoService);
  readonly form = input.required<FormGroup>();
  /** Emits after document changes that are already persisted via API. */
  readonly documentsSynced = output<void>();
  readonly fileTypes = fileTypes;
  readonly documentKind = PlatformDocumentKind;
  readonly uploadType = PlatformDocumentUploadType;
  readonly rowSource = PlatformDocumentTableRowSource;
  readonly importedCvAction = PlatformImportedCvAction;
  readonly TagVariant = TagVariant;
  readonly ChevronState = ChevronState;
  readonly parsingsPerMonth = DOCUMENT_PARSINGS_PER_MONTH;
  readonly icons = {
    paperclip: faPaperclip,
    trash: faTrash,
    eye: faEye,
    star: faStar,
    plus: faPlus,
    upload: faUpload,
    file: faFile,
    fileUser: faFileUser,
    fileLines: faFileLines,
    fileCertificate: faFileCertificate,
  };
  readonly typeOptions: PlatformDocumentTypeOption[] = [
    {
      kind: PlatformDocumentKind.MAIN_CV,
      uploadType: PlatformDocumentUploadType.CV,
      asMain: true,
      icon: faStar,
      labelKey: 'jobboard.platform.profile.sections.documents.main-badge',
    },
    {
      kind: PlatformDocumentKind.CV,
      uploadType: PlatformDocumentUploadType.CV,
      asMain: false,
      icon: faFileUser,
      labelKey: 'jobboard.platform.profile.sections.documents.cv-badge',
    },
    {
      kind: PlatformDocumentKind.COVER_LETTER,
      uploadType: PlatformDocumentUploadType.COVER_LETTER,
      asMain: false,
      icon: faFileLines,
      labelKey: 'jobboard.platform.profile.sections.documents.cover-letter-badge',
    },
    {
      kind: PlatformDocumentKind.CERTIFICATE,
      uploadType: PlatformDocumentUploadType.CERTIFICATE,
      asMain: false,
      icon: faFileCertificate,
      labelKey: 'jobboard.platform.profile.sections.documents.certificate-badge',
    },
    {
      kind: PlatformDocumentKind.OTHER,
      uploadType: PlatformDocumentUploadType.OTHER,
      asMain: false,
      icon: faFile,
      labelKey: 'jobboard.platform.profile.sections.documents.other-badge',
    },
  ];
  readonly uploadControl = new UntypedFormControl(null);
  private readonly uploadInput = viewChild<FileInputComponent>('uploadInput');
  private readonly uploadModal = viewChild.required<ModalComponent>('uploadModal');
  private readonly usageModal = viewChild.required<ModalComponent>('usageModal');
  private readonly viewerModal =
    viewChild.required<PlatformDocumentViewerModalComponent>('viewerModal');

  readonly documents = signal<PlatformDocument[]>([]);
  readonly pendingImportedCvs = signal<PlatformImportedCv[]>([]);
  readonly parsingsRemaining = signal(DOCUMENT_PARSINGS_PER_MONTH);
  readonly uploadKind = signal<PlatformDocumentUploadType | null>(null);
  readonly uploadMode = signal<PlatformDocumentUploadMode>(PlatformDocumentUploadMode.REGULAR);
  readonly uploading = signal(false);
  readonly asMain = signal(true);
  readonly selectedFile = signal<File | null>(null);
  readonly usageDocument = signal<PlatformDocument | null>(null);
  readonly searchQuery = signal<string | null>(null);

  readonly hasMainCv = computed(() =>
    this.documents().some((document) => document.kind === PlatformDocumentKind.MAIN_CV)
  );

  readonly isStandardMainUpload = computed(
    () => this.uploadMode() === PlatformDocumentUploadMode.STANDARD_MAIN
  );

  readonly showAsMainCheckbox = computed(
    () =>
      this.uploadKind() === PlatformDocumentUploadType.CV &&
      !this.isStandardMainUpload() &&
      this.hasMainCv()
  );

  readonly showAutoMainHint = computed(
    () =>
      this.uploadKind() === PlatformDocumentUploadType.CV &&
      !this.isStandardMainUpload() &&
      !this.hasMainCv()
  );

  readonly usageApplications = computed(
    (): PlatformDocumentApplicationUsage[] =>
      this.usageDocument()?.usedInApplications ?? []
  );

  readonly tableRows = computed((): PlatformDocumentTableRow[] => {
    const toDocumentRow = (document: PlatformDocument): PlatformDocumentTableRow => ({
      id: document.id,
      name: document.name,
      source: PlatformDocumentTableRowSource.DOCUMENT,
      document,
      pending: null,
    });
    const mainCvRows = this.documents()
      .filter((document) => document.kind === PlatformDocumentKind.MAIN_CV)
      .map(toDocumentRow);
    const otherDocumentRows = this.documents()
      .filter((document) => document.kind !== PlatformDocumentKind.MAIN_CV)
      .map(toDocumentRow);
    const pendingRows = this.pendingImportedCvs()
      .filter((pending) => pending.action !== PlatformImportedCvAction.DISCARD)
      .map(
        (pending): PlatformDocumentTableRow => ({
          id: pending.id,
          name: pending.name,
          source: PlatformDocumentTableRowSource.PENDING,
          document: null,
          pending,
        })
      );
    return [...mainCvRows, ...pendingRows, ...otherDocumentRows];
  });

  readonly visibleTableRows = computed((): PlatformDocumentTableRow[] => {
    const search = this.searchQuery()?.trim().toLowerCase() ?? '';
    if (!search) {
      return this.tableRows();
    }
    return this.tableRows().filter((row) => {
      if (row.name.toLowerCase().includes(search)) {
        return true;
      }
      if (row.document) {
        const typeLabel = this.transloco
          .translate(this.typeBadgeKey(row.document))
          .toLowerCase();
        return typeLabel.includes(search);
      }
      if (row.pending) {
        const actionLabel = this.transloco
          .translate(this.importedCvActionKey(row.pending.action))
          .toLowerCase();
        return actionLabel.includes(search);
      }
      return false;
    });
  });

  readonly uploadModalTitleKey = computed(() => {
    if (this.isStandardMainUpload()) {
      return 'jobboard.platform.profile.sections.documents.upload.modal-title-standard';
    }
    switch (this.uploadKind()) {
      case PlatformDocumentUploadType.CV:
        return 'jobboard.platform.profile.sections.documents.upload.modal-title-cv';
      case PlatformDocumentUploadType.COVER_LETTER:
        return 'jobboard.platform.profile.sections.documents.upload.modal-title-cover-letter';
      case PlatformDocumentUploadType.CERTIFICATE:
        return 'jobboard.platform.profile.sections.documents.upload.modal-title-certificate';
      case PlatformDocumentUploadType.OTHER:
      default:
        return 'jobboard.platform.profile.sections.documents.upload.modal-title-other';
    }
  });

  readonly parsingsUsed = computed(() =>
    Math.max(0, this.parsingsPerMonth - this.parsingsRemaining())
  );

  readonly parsingsMeterValue = computed((): MeterItem[] => [
    {
      label: this.transloco.translate(
        'jobboard.platform.profile.sections.documents.parsings.available-label'
      ),
      value: this.parsingsRemaining(),
      color: PARSINGS_METER_COLORS.available,
    },
    {
      label: this.transloco.translate(
        'jobboard.platform.profile.sections.documents.parsings.used-label'
      ),
      value: this.parsingsUsed(),
      color: PARSINGS_METER_COLORS.used,
    },
  ]);

  constructor() {
    effect((onCleanup) => {
      const form = this.form();
      this.documents.set(
        (form.controls['documents'].value as PlatformDocument[]) ?? []
      );
      this.pendingImportedCvs.set(
        (form.controls['pendingImportedCvs'].value as PlatformImportedCv[]) ?? []
      );
      this.parsingsRemaining.set(
        (form.controls['documentParsingsRemaining'].value as number) ??
          DOCUMENT_PARSINGS_PER_MONTH
      );
      const documentsSub = form.controls['documents'].valueChanges.subscribe(
        (value) => this.documents.set((value as PlatformDocument[]) ?? [])
      );
      const pendingSub = form.controls['pendingImportedCvs'].valueChanges.subscribe(
        (value) =>
          this.pendingImportedCvs.set((value as PlatformImportedCv[]) ?? [])
      );
      const remainingSub = form.controls[
        'documentParsingsRemaining'
      ].valueChanges.subscribe((value) =>
        this.parsingsRemaining.set(
          (value as number) ?? DOCUMENT_PARSINGS_PER_MONTH
        )
      );
      onCleanup(() => {
        documentsSub.unsubscribe();
        pendingSub.unsubscribe();
        remainingSub.unsubscribe();
      });
    });
  }

  /**
   * Tracks table rows by source and id.
   * @param _index - Row index
   * @param row - Table row
   * @returns Stable row key
   */
  trackByRow(_index: number, row: PlatformDocumentTableRow): string {
    return `${row.source}-${row.id}`;
  }

  /**
   * Updates the free-text search for the documents table.
   * @param search - Search query or null
   */
  onSearch(search: string | null): void {
    this.searchQuery.set(search);
  }

  /**
   * Returns the translation key for an imported document action badge.
   * @param action - Imported CV action
   * @returns Translation key
   */
  importedCvActionKey(action: PlatformImportedCvAction): string {
    switch (action) {
      case PlatformImportedCvAction.MAIN:
        return 'jobboard.platform.profile.sections.documents.main-badge';
      case PlatformImportedCvAction.CV:
        return 'jobboard.platform.profile.sections.documents.cv-badge';
      case PlatformImportedCvAction.COVER_LETTER:
        return 'jobboard.platform.profile.sections.documents.cover-letter-badge';
      case PlatformImportedCvAction.CERTIFICATE:
        return 'jobboard.platform.profile.sections.documents.certificate-badge';
      case PlatformImportedCvAction.OTHER:
        return 'jobboard.platform.profile.sections.documents.other-badge';
      default:
        return 'jobboard.platform.profile.sections.documents.pending-badge';
    }
  }

  /**
   * Maps a type option to the imported-CV action stored on pending rows.
   * @param option - Type option
   * @returns Imported CV action
   */
  typeOptionToImportedAction(
    option: PlatformDocumentTypeOption
  ): PlatformImportedCvAction {
    switch (option.kind) {
      case PlatformDocumentKind.MAIN_CV:
        return PlatformImportedCvAction.MAIN;
      case PlatformDocumentKind.CV:
        return PlatformImportedCvAction.CV;
      case PlatformDocumentKind.COVER_LETTER:
        return PlatformImportedCvAction.COVER_LETTER;
      case PlatformDocumentKind.CERTIFICATE:
        return PlatformImportedCvAction.CERTIFICATE;
      default:
        return PlatformImportedCvAction.OTHER;
    }
  }

  /**
   * Whether a type option matches the pending import action.
   * @param action - Pending action
   * @param option - Type option
   * @returns Whether selected
   */
  isImportedActionSelected(
    action: PlatformImportedCvAction,
    option: PlatformDocumentTypeOption
  ): boolean {
    return this.typeOptionToImportedAction(option) === action;
  }

  /**
   * Returns the type badge translation key for a document row.
   * @param document - Document row
   * @returns Translation key
   */
  typeBadgeKey(document: PlatformDocument): string {
    switch (document.kind) {
      case PlatformDocumentKind.MAIN_CV:
        return 'jobboard.platform.profile.sections.documents.main-badge';
      case PlatformDocumentKind.CV:
        return 'jobboard.platform.profile.sections.documents.cv-badge';
      case PlatformDocumentKind.COVER_LETTER:
        return 'jobboard.platform.profile.sections.documents.cover-letter-badge';
      case PlatformDocumentKind.CERTIFICATE:
        return 'jobboard.platform.profile.sections.documents.certificate-badge';
      default:
        return 'jobboard.platform.profile.sections.documents.other-badge';
    }
  }

  /**
   * Whether a type option is the currently selected document type.
   * @param document - Document row
   * @param option - Type option
   * @returns Whether the option matches
   */
  isTypeSelected(document: PlatformDocument, option: PlatformDocumentTypeOption): boolean {
    return document.kind === option.kind;
  }

  /**
   * Updates the decision for an imported CV.
   * @param cvId - Imported CV id
   * @param action - Selected action
   */
  setImportedCvAction(cvId: number, action: PlatformImportedCvAction): void {
    const next = this.pendingImportedCvs().map((cv) => {
      if (cv.id === cvId) {
        return { ...cv, action };
      }
      if (
        action === PlatformImportedCvAction.MAIN &&
        cv.action === PlatformImportedCvAction.MAIN
      ) {
        return { ...cv, action: PlatformImportedCvAction.UNSET };
      }
      return cv;
    });
    this.patchControl('pendingImportedCvs', next);
  }

  /**
   * Opens the upload modal for the selected document type.
   * @param kind - Upload type
   */
  openUploadModal(kind: PlatformDocumentUploadType): void {
    this.uploadMode.set(PlatformDocumentUploadMode.REGULAR);
    this.uploadKind.set(kind);
    this.uploadControl.setValue(null);
    this.selectedFile.set(null);
    this.asMain.set(true);
    this.uploadInput()?.reset();
    this.uploadModal().open();
  }

  /**
   * Opens the dedicated standard-CV upload modal without checkbox.
   */
  openStandardMainUploadModal(): void {
    this.uploadMode.set(PlatformDocumentUploadMode.STANDARD_MAIN);
    this.uploadKind.set(PlatformDocumentUploadType.CV);
    this.uploadControl.setValue(null);
    this.selectedFile.set(null);
    this.asMain.set(true);
    this.uploadInput()?.reset();
    this.uploadModal().open();
  }

  /**
   * Resets upload modal state after dismiss.
   */
  onUploadModalClose(): void {
    this.uploadKind.set(null);
    this.uploadMode.set(PlatformDocumentUploadMode.REGULAR);
    this.uploadControl.setValue(null);
    this.selectedFile.set(null);
    this.asMain.set(true);
    this.uploading.set(false);
    this.uploadInput()?.reset();
  }

  /**
   * Syncs the selected file into local modal state.
   */
  onUploadFileChange(): void {
    const value = this.uploadControl.value as File | File[] | null;
    const file = Array.isArray(value) ? (value[0] ?? null) : value;
    this.selectedFile.set(file);
  }

  /**
   * Uploads the selected file from the modal.
   */
  submitUpload(): void {
    const kind = this.uploadKind();
    const file = this.selectedFile();
    if (!kind || !file || this.uploading()) {
      return;
    }
    this.uploading.set(true);
    const forceMain =
      this.isStandardMainUpload() ||
      (kind === PlatformDocumentUploadType.CV && !this.hasMainCv());
    const asMain =
      kind === PlatformDocumentUploadType.CV
        ? forceMain || this.asMain()
        : false;
    const request =
      kind === PlatformDocumentUploadType.CV
        ? this.profile.uploadCv(file, asMain)
        : this.profile.uploadDocument(file, kind);
    request.subscribe({
      next: () => {
        this.patchPersistedDocuments();
        this.uploading.set(false);
        this.uploadModal().close();
      },
      error: () => this.uploading.set(false),
    });
  }

  /**
   * Opens the document preview modal.
   * @param document - Document id and file name to preview
   */
  previewDocument(document: Pick<PlatformDocument, 'id' | 'name'>): void {
    this.viewerModal().open(document);
  }

  /**
   * Opens the modal listing applications that use this document.
   * @param document - Document row
   */
  openUsageModal(document: PlatformDocument): void {
    this.usageDocument.set(document);
    this.usageModal().open();
  }

  /**
   * Clears usage modal state after dismiss.
   */
  onUsageModalClose(): void {
    this.usageDocument.set(null);
  }

  /**
   * Applies a type option to a document (including standard CV).
   * @param documentId - Document id
   * @param option - Selected type option
   */
  selectDocumentType(documentId: number, option: PlatformDocumentTypeOption): void {
    const request = option.asMain
      ? this.profile.updateDocument(documentId, { type: 'cv', main: true })
      : this.profile.updateDocument(documentId, {
          type: option.uploadType,
          main: false,
        });
    request.subscribe({
      next: () => this.patchPersistedDocuments(),
    });
  }

  /**
   * Removes a document from the profile.
   * @param documentId - Document id
   */
  removeDocument(documentId: number): void {
    this.profile.deleteDocument(documentId).subscribe({
      next: () => this.patchPersistedDocuments(),
    });
  }

  /**
   * Discards an unassigned imported CV so it is not taken over.
   * @param cvId - Imported CV upload id
   */
  discardPending(cvId: number): void {
    const next = this.pendingImportedCvs().map((cv) =>
      cv.id === cvId ? { ...cv, action: PlatformImportedCvAction.DISCARD } : cv
    );
    this.patchControl('pendingImportedCvs', next);
  }

  /**
   * Patches an array control and marks the parent form dirty.
   * @param name - Control name
   * @param value - Next value
   */
  private patchControl(name: string, value: unknown): void {
    this.form().controls[name].setValue(value);
    this.form().controls[name].markAsDirty();
  }

  /**
   * Syncs already-persisted document fields into the form without marking it dirty.
   */
  private patchPersistedDocuments(): void {
    const profile = this.profile.profile();
    this.form().controls['documents'].setValue(profile.documents);
    this.form().controls['documentParsingsRemaining'].setValue(
      profile.documentParsingsRemaining
    );
    this.documentsSynced.emit();
  }
}
